import { commitItemEdits, proposeItemEdits, readChangeSetHead, type ItemEdit, type ProposalOutcome } from "../../api/content/changeSetsApi";
import { notifyChangeSetsChanged } from "./changeSetEvents";

/** Where a proposal goes: a new change set, or one the user already has open. */
export type ChangeSetChoice =
  | { readonly kind: "new"; readonly title: string }
  | { readonly kind: "existing"; readonly changeSetId: string };

/**
 * The edits of one commit, built for the head they apply to: `null` for the first commit
 * of a new change set (it applies to `main`), else the change set's head. A later commit
 * can read what an earlier one wrote at that head, such as the version of a new prompt a
 * step pins (authoring-ui.md AU-3: several drafts in one change set, in a row).
 */
export type CommitBuilder = (head: string | null) => Promise<readonly ItemEdit[]>;

/**
 * Commit each builder's edits in order into the chosen change set. A builder that returns no
 * edits makes no commit. A new change set opens with the first builder's edits that change
 * something; until then each builder builds on `main`. If none does, none opens: nothing to
 * change. A commit tells the lists of change sets, here and in the other pane.
 */
export async function commitToChangeSet(projectId: string, choice: ChangeSetChoice, builders: readonly CommitBuilder[]): Promise<ProposalOutcome> {
  let changeSetId: string | null = choice.kind === "existing" ? choice.changeSetId : null;
  for (const build of builders) {
    if (changeSetId === null && choice.kind === "new") {
      const edits = await build(null);
      const proposed = edits.length === 0 ? null : await proposeItemEdits(projectId, choice.title, edits);
      changeSetId = proposed?.kind === "proposed" ? proposed.changeSetId : null;
      continue;
    }
    if (changeSetId === null) {
      throw new Error("A change set to commit to is missing.");
    }
    const head = await readChangeSetHead(changeSetId);
    const edits = await build(head);
    if (edits.length > 0) {
      await commitItemEdits(changeSetId, head, edits);
    }
  }
  if (changeSetId === null) return { kind: "nothingToChange" };
  notifyChangeSetsChanged();
  return { kind: "proposed", changeSetId };
}
